import { useState, type ReactNode } from 'react';
import { SidebarProvider } from '@/components/ui/sidebar';
import { usePersistedSidebarOpen } from '@/hooks/useSidebarPreferences';
import { TransientSidebarCollapseContext } from './transient-sidebar-collapse';

/**
 * SidebarProvider that remembers desktop icon-collapse the same way section
 * collapse is remembered: user preference + local cache, cookie as fallback.
 *
 * A page can also collapse it for a while (see `useTransientSidebarCollapse`)
 * without that becoming the saved preference. Expanding it by hand ends the
 * override, and only a real change of the saved state is written.
 */
export function PersistedSidebarProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { open, onOpenChange } = usePersistedSidebarOpen();
  const [transientlyCollapsed, setTransientlyCollapsed] = useState(false);

  const handleOpenChange = (next: boolean) => {
    setTransientlyCollapsed(false);
    if (next !== open) onOpenChange(next);
  };

  return (
    <TransientSidebarCollapseContext.Provider value={setTransientlyCollapsed}>
      <SidebarProvider
        open={open && !transientlyCollapsed}
        onOpenChange={handleOpenChange}
      >
        {children}
      </SidebarProvider>
    </TransientSidebarCollapseContext.Provider>
  );
}
